import React, { useState } from 'react';
import {
  Box,
  TextField,
  Autocomplete,
  Typography,
  IconButton,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Save, Cancel, Edit } from '@mui/icons-material';
import { useNotify, useTranslate } from 'react-admin';
import { apiClient } from '../../utils/apiClient';
import { API_CONFIG } from '../../config/api';

const FALLBACK_TIMEZONE_VALUES = [
  'UTC', 'Asia/Hong_Kong', 'Asia/Shanghai', 'Asia/Taipei', 'Asia/Tokyo', 'Asia/Seoul',
  'Asia/Singapore', 'Asia/Bangkok', 'Asia/Manila', 'Asia/Kuala_Lumpur', 'Asia/Jakarta',
  'Asia/Dubai', 'Asia/Kolkata', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Australia/Sydney', 'Pacific/Auckland',
];

const offsetLabel = (tz: string): string => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset' as any,
    }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value || '';
  } catch {
    return '';
  }
};

const buildTimezoneOptions = (): { value: string; label: string }[] => {
  let zones: string[] = [];
  try {
    const supported = (Intl as any).supportedValuesOf?.('timeZone');
    if (Array.isArray(supported) && supported.length > 0) zones = supported as string[];
  } catch {
    zones = [];
  }
  if (zones.length === 0) zones = FALLBACK_TIMEZONE_VALUES;
  if (!zones.includes('UTC')) zones = ['UTC', ...zones];
  return zones
    .map((tz) => {
      const off = offsetLabel(tz);
      const name = tz.replace(/_/g, ' ');
      return { value: tz, label: off ? `${name} (${off})` : name };
    })
    .sort((a, b) => a.value.localeCompare(b.value));
};

// Full IANA timezone list (from Intl.supportedValuesOf when available, else a
// curated fallback for older runtimes).
const TIMEZONE_OPTIONS = buildTimezoneOptions();

interface TimezoneSelectorProps {
  currentValue: string;
  canEdit: boolean;
  onSave: (value: string) => Promise<void>;
}

export const TimezoneSelector: React.FC<TimezoneSelectorProps> = ({
  currentValue,
  canEdit,
  onSave,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(currentValue);
  const [saving, setSaving] = useState(false);
  const notify = useNotify();
  const translate = useTranslate();

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setValue(currentValue);
  };

  const handleSave = async () => {
    if (!value || value === currentValue) {
      setIsEditing(false);
      return;
    }

    setSaving(true);
    try {
      await onSave(value);
      setIsEditing(false);
      notify(translate('settings.timezone.updated'), { type: 'success' });
    } catch (error) {
      notify(translate('settings.timezone.updateFailed'), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Get current timezone display
  const currentTimezone = TIMEZONE_OPTIONS.find(tz => tz.value === currentValue);
  const currentTime = new Date().toLocaleString('en-GB', {
    timeZone: currentValue,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  if (isEditing) {
    return (
      <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Autocomplete
          value={TIMEZONE_OPTIONS.find(tz => tz.value === value) || null}
          onChange={(_, newValue) => setValue(newValue?.value || currentValue)}
          options={TIMEZONE_OPTIONS}
          getOptionLabel={(option) => option.label}
          sx={{ flex: 1, minWidth: 0 }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              fullWidth
              sx={{ minWidth: { xs: 0, sm: 400 }, width: '100%' }}
            />
          )}
          disabled={saving}
        />
        <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
          <IconButton
            onClick={handleSave}
            color="primary"
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : <Save />}
          </IconButton>
          <IconButton onClick={handleCancel} disabled={saving}>
            <Cancel />
          </IconButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <Box>
        <Typography variant="body1">
          {currentTimezone?.label || currentValue}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {translate('settings.timezone.currentTime')}: {currentTime}
        </Typography>
      </Box>
      {canEdit && (
        <IconButton onClick={handleEdit} size="small" color="primary">
          <Edit fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};